/*
 * Purpose: Button primitives — primary, secondary, tertiary, ghost action tiers
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { resolveActionTierProps, type ActionTier } from "@/lib/actionHierarchy"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[transform,box-shadow,background-color,border-color,color,filter] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ab-green-core/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.985]",
  {
    variants: {
      variant: {
        default:
          "ui-lift ui-lift-accent bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "ui-lift bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "ui-lift border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "ui-lift-subtle ui-btn-secondary-action border border-border/70 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90 hover:border-foreground/12",
        ghost:
          "ui-btn-ghost-action border border-transparent bg-transparent text-muted-foreground hover:bg-muted/45 hover:text-foreground hover:border-border/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Semantic tier — maps to variant + hierarchy classes. Ignored when variant is destructive. */
  actionTier?: ActionTier
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, actionTier, ...props }, ref) => {
    const tierProps = actionTier && variant !== 'destructive' ? resolveActionTierProps(actionTier) : null
    const resolvedVariant = tierProps?.variant ?? variant
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant: resolvedVariant, size }), tierProps?.className, className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
